import canopyUrls from 'canopy-urls!sofe';
import { truncateFilename } from 'src/documents/documents.helpers.js';
import { uuid } from 'src/common/helpers.js';
import { fetchAsObservable } from 'fetcher!sofe';
import { catchError, map, pluck } from 'rxjs/operators';
import { catchSyncStacktrace, catchAsyncStacktrace, asyncStacktrace } from 'auto-trace';
import { warningToast } from 'toast-service!sofe';
import { getS3Path, createS3FileName } from 'src/documents/documents.resource';
import { headers } from 'src/common/headers.constant';

export function getListOfFilesInFolder(folderId) {
  // api/docs/folders/{file_id_of_folder}/files
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/folders/${folderId}/files?includes=name_path`);
}

export function getSingleContactInboxFiles(contactId) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/folders/INBOX/files?includes=name_path&contact_id=${contactId}`)
    .pipe(
      pluck('files'),
    )
}

export function getFileMeta(fileId, includeOptions={}) {
  // GET /api/docs/files/:id
  const includesList = [
    includeOptions.includePreview && 'preview_links',
    includeOptions.includeAnnotations && 'annotations',
  ].filter(item => item);

  const includes = includesList.length ? `?includes=${includesList.join(',')}` : '';
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files/${fileId}${includes}`)
    .pipe(
      map(resp => resp.file)
    )
}

export function deleteFiles(fileIds) {
  // api/docs/files:batch_delete
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files:batch-delete`,
    {
      method: 'POST',
      headers,
      body: `{"file_ids": ${JSON.stringify(fileIds)}}`
    }
  );
}

export function createFolder(parentFolderId, folderName, permissions) {
  // api/docs/folders/{file_id_of_folder}/folders
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/folders/${parentFolderId}/folders`, {
    method: 'POST',
    headers,
    body: { folder_file: { name: folderName, permissions: permissions } }
  });
}

export function patchFile(fileId, fileData) {
  // api/docs/files/{file_id}
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files/${fileId}`,
    {
      method: 'PATCH',
      headers,
      body: `{"file": ${JSON.stringify(fileData)}}`
    }
  );
}

export function getRecentlyViewedFiles(excludeContactFiles, restrictToOneContact) {
  return globalFilesSearch(null, null, null, true, excludeContactFiles, restrictToOneContact);
}

export function globalFilesSearch(folderId, searchVal, searchDeleted, recents, excludeContactFiles, restrictToOneContact) {
  folderId = folderId ? `folder_id=${folderId}` : '';
  searchVal = searchVal ? `search_term=${searchVal}` : '';
  searchDeleted = searchDeleted ? `only_deleted=${searchDeleted}` : '';
  recents = recents ? `recent=true` : '';
  excludeContactFiles = excludeContactFiles ? `contact_id_plus=exclude_contacts` : '';
  restrictToOneContact = restrictToOneContact ? `contact_id_plus=CON${restrictToOneContact}` : '';

  const queryArr = [folderId, searchVal, searchDeleted, recents, excludeContactFiles, restrictToOneContact].filter(query => !!query);
  const queryStr = queryArr ? `?${queryArr.join('&')}` : '';

  // api/docs/search
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/search${queryStr}`)
    .pipe(
      pluck('files')
    );
}

export function patchFileName(fileId, newName){
  return patchFile(fileId, {name: newName});
}

export function patchFileDescription(fileId, description){
  return patchFile(fileId, {description: description});
}

export function moveFiles(fileIds, targetFolder, excludeNamePath=null, allowCrossContact=false){
  // api/docs/files:move
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files:move?includes=${excludeNamePath ? '' : 'name_path'}`,
    {
      method: 'POST',
      headers,
      body: {
        files: {
          allow_cross_contact: allowCrossContact,
          file_ids: fileIds,
          new_parent_id: targetFolder
        }
      }
    }
  );
}

export function patchFileVisibility(fileIds, visible){
  // api/docs/files:batch-visibility
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files:batch-visibility`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({"files": {"file_ids": fileIds, "is_visible": visible}}),
    }
  );
}

export function createZip(fileIds) {
  // api/docs/zips
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/zips`,
    {
      method: 'POST',
      headers,
      body: `{"file_ids": ${JSON.stringify(fileIds)}}`
    }
  )
  .pipe(
    map(res => res.hash),
    catchError(catchAsyncStacktrace())
  )
}

export function getZip(zipId) {
  // api/docs/zips/{zip_id}
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/zips/${zipId}`)
}

export function unZipFile(fileId){
  // api/docs/files/{file_id}:extract
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/files/${fileId}:extract`,
    {
      method: 'POST',
    }
  );
}

export function getDeletedFiles(rootFolderId) {
  // /api/docs/trash/{file_id_of_folder}
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/trash/${rootFolderId}`)
}

function getS3UploadUrl(folder) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/folders/${folder.id}/s3`)
    .pipe(
      map(resp => resp.s3)
    )
}

function uploadFileMeta(parentFolder, file, s3Data, inbox, hidden, days_until_expiration) {
  const s3FileName = createS3FileName(file);
  const pathToS3 = getS3Path(s3Data.fields.key, s3FileName);
  let fileData = {
    name: file.filename,
    path_to_s3: pathToS3,
    description: '',
    mimetype: file.file.type || 'other',
    is_visible: false,
    filesize: file.file.size || 0,
    inbox,
    hidden,
    ...(days_until_expiration && {days_until_expiration: days_until_expiration}),
  };

  return fetchAsObservable(
    `${canopyUrls.getWorkflowUrl()}/api/docs/folders/${parentFolder.id}/files`,
    {
      method: 'POST',
      headers,
      body: `{"file" : ${JSON.stringify(fileData)}}`,
    }
  )
    .pipe(
      catchError(error => {
        throw new Error(`Problem storing uploaded file, ${JSON.stringify(fileData)}. This file is stored now in s3, but not in DDG. Err ${JSON.stringify(error)}`);
      })
    )
}

function uploadToS3(file, s3Data) {
  // For s3 we change the file name to include the uuid
  const otherFields = s3Data.fields;
  const s3FileName = createS3FileName(file);
  const s3Path = getS3Path(otherFields.key, s3FileName);
  const uploadFields = {...otherFields, key: s3Path};

  // s3 needs the body in form data
  const formData = new FormData();
  for (let field in uploadFields) formData.append(field, uploadFields[field]);
  formData.append('file', file.file);
  return fetchAsObservable(s3Data.url,
    {
      method: 'POST',
      credentials: 'omit',
      body: formData,
    }
  )
    .pipe(
      map(resp => file),
      catchError(ex => {
        ex.toastMessage = `Failed to upload file "${file.filename}."`;
        catchSyncStacktrace(ex);
        throw ex
      })
    )
}

export function uploadFiles(config) {
  const { files = [], folderId, inbox, hidden, callback, days_until_expiration } = config
  const tweakedFiles = files.map(file => ({file: file, uuid: uuid(), filename: truncateFilename(file.name)}))
  return filesUploadAsync(tweakedFiles, {id: folderId}, inbox, callback, hidden, days_until_expiration)
}

export async function filesUploadAsync(filesAndUuids, parentFolder, inbox, uploadedFilesCallback = () => { }, hidden = false, days_until_expiration) {
  //POST files to S3
  if(filesAndUuids.length) {
    //Request information from S3 about where to upload file(s)
    const s3Data = await new Promise((resolve, reject) => {
      getS3UploadUrl(parentFolder).subscribe(
        s3Info => resolve(s3Info),
        err => reject(new Error(`Failed to get s3 upload info for folder "${parentFolder.name}." Err: ${JSON.stringify(err)}`))
      )
    });

    const s3Response = await Promise.all(filesAndUuids.map(async file => {
      return await new Promise((resolve, reject) => {
        uploadToS3(file, s3Data).subscribe(
          resp => resolve({...resp, uuid: file.uuid}),
          err => {
            warningToast(`Failed to upload file "${file.filename}.`);
            resolve(null)
          }
        )
      });
    }));

    //If the s3 process was a success, then POST file metadata
    return await Promise.all(s3Response.map(async file => {
      const uploadedFile = await new Promise((resolve, reject) => {
        uploadFileMeta(parentFolder, file, s3Data, inbox, hidden, days_until_expiration).subscribe(
          resp => resolve({...resp.file, uuid: file.uuid}),
          err => {
            warningToast(`Failed to upload file "${file.filename}.`);
            resolve(null)
          }
        )
      });
      const s3FileName = createS3FileName(file);
      const pathToS3 = getS3Path(s3Data.fields.key, s3FileName);
      let returnedUploadedFile = {...uploadedFile, path_to_s3: pathToS3}
      uploadedFilesCallback([returnedUploadedFile]);
      return returnedUploadedFile;
    }));
  }
}

export function getInboxCount() {
  // api/docs/inbox_count
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/docs/inbox_count`);
}

export function purgeFiles(file_ids) {
  // api/docs/files:purge
  return fetchAsObservable(`/api/docs/files:purge`, {
    method: 'POST',
    headers,
    body: JSON.stringify({file_ids}),
  })
  .pipe(
    catchError(asyncStacktrace(err => {
      err.toastMessage = "An error occurred while permanently deleting your file. Please try again.";
      catchSyncStacktrace(err);
    }))
  );
}

export function copyFiles(fileIds, folderId = '0', hidden = false, inbox = false) {
  const body = {
    files: {
      file_ids: fileIds,
      new_parent_id: folderId,
      hidden,
      inbox,
    }
  }

  return fetchAsObservable(`/api/docs/files:copy`,
    {
      method: 'POST',
      headers,
      body,
    }
  );
}
